import * as React from "react";
import { ClientOffer, ServerOffer } from "../types/offers";
import { WebshopPlayerInfo } from "../api/app-server";
import { Currencies, Offers } from "../DataModels";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShopState {
  /** Raw server offers as returned by the app-server, if available. */
  serverOffers: ServerOffer[] | undefined;
  /** Transformed, display-ready offers. */
  clientOffers: ClientOffer[];
  /** Legacy Offers shape — transitional until all section components migrate to ClientOffer[]. */
  legacyOffers: Offers | undefined;
  /** Player currencies from the most recent webshop fetch. */
  currencies: Currencies | undefined;
  /** Player currency and progression data from the most recent webshop fetch. */
  playerInfo: WebshopPlayerInfo | null;
  isLoading: boolean;
  error: Error | null;
  /** ISO timestamp of the last successful fetch, or null if never fetched. */
  lastFetchedAt: string | null;
  /** True while a purchase network call is in flight. */
  isPurchasing: boolean;
  /** The error from the most recent failed purchase attempt, if any. */
  purchaseError: Error | null;
}

// ---------------------------------------------------------------------------
// Context value shape
// ---------------------------------------------------------------------------

interface ShopContextValue {
  state: ShopState;
  setState: React.Dispatch<React.SetStateAction<ShopState>>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ShopContext = React.createContext<ShopContextValue | undefined>(undefined);
ShopContext.displayName = "ShopContext";

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const INITIAL_STATE: ShopState = {
  serverOffers: undefined,
  clientOffers: [],
  legacyOffers: undefined,
  currencies: undefined,
  playerInfo: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  isPurchasing: false,
  purchaseError: null,
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ShopProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, setState] = React.useState<ShopState>(INITIAL_STATE);

  const value = React.useMemo<ShopContextValue>(
    () => ({ state, setState }),
    [state, setState]
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useShopContext(): ShopContextValue {
  const ctx = React.useContext(ShopContext);
  if (ctx === undefined) {
    throw new Error("useShopContext must be used within a ShopProvider");
  }
  return ctx;
}

export { ShopContext };
